/** Staff: Social Posting — Ox Resin's own Facebook/Instagram pages, ready-made posts for installers, installer activity, Meta set-up guide. */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Share2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { wget } from '@/components/website/api';
import { ConnectedAccounts } from '@/components/social/ConnectedAccounts';
import { SocialComposer } from '@/components/social/SocialComposer';
import { PostHistory } from '@/components/social/PostHistory';
import { SuggestionManager } from '@/components/social/SuggestionManager';
import { MetaSetupGuide } from '@/components/social/MetaSetupGuide';
import { InsightsSummary } from '@/components/social/PostMetrics';

export default function StaffSocial() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [posts, setPosts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [overview, setOverview] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p, sg, ov] = await Promise.all([wget('/social/status'), wget('/social/posts'), wget('/staff/social/suggestions'), wget('/staff/social/overview')]);
      setStatus(s); setPosts(p); setSuggestions(sg); setOverview(ov);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('connect');
    if (q === 'ok') toast.success('Facebook connected — pages listed below');
    if (q === 'cancelled') toast.error('Facebook login was cancelled');
    if (q === 'failed') toast.error('Facebook connection failed — check the app settings and try again');
    if (q) navigate('/staff/social', { replace: true });
  }, [location.search, navigate]);
  useEffect(() => {
    if (!posts.some((p) => p.status === 'publishing')) return undefined;
    const t = setInterval(() => wget('/social/posts').then(setPosts).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [posts]);

  if (!status) return <div className="min-h-screen bg-gray-50 p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-social">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" data-testid="staff-social-back"><ArrowLeft className="w-4 h-4" /> Staff Dashboard</button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Share2 className="w-6 h-6 text-blue-700" /> Social Posting</h1><p className="text-sm text-gray-500 mt-1">Post to Ox Resin's Facebook &amp; Instagram pages, and publish ready-made posts installers can use on theirs.</p></div>
          {overview && <div className="text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5 inline-flex items-center gap-2" data-testid="staff-social-overview"><Users className="w-3.5 h-3.5" /> {overview.installers_connected} installer{overview.installers_connected === 1 ? '' : 's'} connected · {overview.installer_posts} installer post{overview.installer_posts === 1 ? '' : 's'}</div>}
        </div>
        <ConnectedAccounts status={status} onChanged={load} staff />
        <SocialComposer accounts={status.accounts} prefill={prefill} onConsumePrefill={() => setPrefill(null)} onPosted={() => load()} />
        <SuggestionManager items={suggestions} onChanged={load} />
        <InsightsSummary posts={posts} />
        <PostHistory posts={posts} onChanged={load} />
        {overview?.recent?.length > 0 && <div data-testid="staff-social-installer-posts"><h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Recent installer posts</h2><PostHistory posts={overview.recent} onChanged={load} showOwner /></div>}
        <MetaSetupGuide redirectUri={status.redirect_uri} configured={status.configured} />
      </div>
    </div>
  );
}
